import type * as React from 'react';
import { type JSX, useState } from 'react';
import titleTemplate from 'lib/title-template';
import Head from 'next/head';
import Page from 'components/common/page';
import { useRouter } from 'next/router';
import type { FailedSsoCode, SignupCode } from 'api/lib/auth-codes';
import Link from 'next/link';
import { useAppDispatch } from 'redux/store';
import { signup } from 'redux/modules/auth-module';
import Floating from 'components/common/floating';
import Button from 'components/common/button';
import GoogleSVG from 'static/svgs/google.svg';
import AppleSVG from 'static/svgs/apple.svg';
import makeSsoUrl from 'web/lib/make-sso-url';

const failedSsoMessages: Record<FailedSsoCode, string> = {
  missing_email: 'An email address is required to continue.',
  unknown: 'Something went wrong.',
};
const failedSignupMessages: Record<SignupCode, string> = {
  email_taken: 'This email is already taken. Please log in.',
  invalid_password: 'Your password has to be between 8 and 64 characters.',
  invalid_email: 'Please enter a valid email.',
};

const title =
  'font-display text-[1.75rem] leading-tight font-semibold tracking-[-0.01em] text-ink';
const label = 'mt-5 mb-1.5 block text-sm font-semibold text-ink';
const footLink = 'inline-flex min-h-11 items-center text-accent-text';

const Signup = (): JSX.Element => {
  const [email, setEmail] = useState<string | null>(null);
  const [password, setPassword] = useState<string | null>(null);
  const router = useRouter();
  const queryErrorCode = (
    Array.isArray(router.query.error)
      ? router.query.error[0]
      : router.query.error
  ) as FailedSsoCode;
  const required =
    router.query.required && 'Please create an account to continue.';
  const initialErrorMessage =
    failedSsoMessages[queryErrorCode] || required || null;
  const [stateErrorMessage, setErrorMessage] = useState<string | null>(null);
  const errorMessage = stateErrorMessage || initialErrorMessage;
  const dispatch = useAppDispatch();

  const handleSubmit = async (event: React.MouseEvent): Promise<void> => {
    event.preventDefault();
    if (!(email && password)) {
      return;
    }

    try {
      await dispatch(signup({ email, password })).unwrap();
      router.push((router.query.next as string) || '/');
    } catch (error) {
      setErrorMessage(
        failedSignupMessages[
          (error as { code: SignupCode })?.code || 'unknown'
        ] || 'Something went wrong.',
      );
    }
  };

  return (
    <Page pageName="Signup">
      <Head>
        <title>{titleTemplate('Sign up')}</title>
        <meta
          name="description"
          content="Create a Bibcitation account to take advantage of our latest features, such as managing up to 20 citation lists or sharing them with other users."
        />
      </Head>
      <div className="px-4">
        <Floating>
          <form>
            <h1 className={title}>Sign up</h1>
            {errorMessage && (
              <div
                role="alert"
                className="mt-5 rounded-xl bg-danger-soft px-4 py-3 text-15 text-danger"
              >
                {errorMessage}
              </div>
            )}
            <label className={label} htmlFor="email">
              Email
            </label>
            <input
              type="email"
              id="email"
              onChange={(event) => setEmail(event.target.value)}
            />
            <label className={label} htmlFor="password">
              Password
            </label>
            <input
              type="password"
              id="password"
              onChange={(event) => setPassword(event.target.value)}
            />
            <div className="mt-6">
              <Button fullWidth type="brand" onClick={handleSubmit}>
                Sign up
              </Button>
            </div>
          </form>
          <div className="my-5 h-px bg-line" />
          <div className="flex flex-col gap-3">
            <Button
              fullWidth
              type="outline"
              href={makeSsoUrl(router.query.next, 'google', 'signup')}
            >
              <GoogleSVG />
              Continue with Google
            </Button>
            <Button
              fullWidth
              type="outline"
              href={makeSsoUrl(router.query.next, 'apple', 'signup')}
            >
              <AppleSVG className="fill-current" />
              Continue with Apple
            </Button>
          </div>
          <div className="mt-6 text-center text-15">
            <Link
              className={footLink}
              href={`/login?next=${encodeURIComponent(
                (router.query.next as string) || '/',
              )}`}
            >
              Log in
            </Link>
          </div>
        </Floating>
      </div>
    </Page>
  );
};
export default Signup;
